import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  type ReactNode,
  type MouseEventHandler
} from 'react';
import { Loader, Combobox, ComboboxOption } from '@deque/cauldron-react';
import { useTranslation } from 'react-i18next';
import type { ProjectsByConnections } from '../../../../../hooks/useIntegrationProjectsFromTemplates';
import type { IntegrationConnection } from '../../../../../utils/integrations-client/jira/connections';
import type { IntegrationTemplate } from '../../../../../api-client';
import { ProductNames } from '../../../../../constants';
import styles from './ChooseTemplate.css';

type TextWithMatchesProps = {
  text: string;
  inputValue: string;
};

const TextWithMatches = ({ text, inputValue }: TextWithMatchesProps) => {
  const matchStart = text.toLowerCase().indexOf(inputValue.toLowerCase());

  const matchLength = inputValue.length;
  const matchBefore = text.substring(0, matchStart);
  const match = text.substring(matchStart, matchLength + matchStart);
  const matchAfter = text.substring(matchStart + matchLength);

  return (
    <>
      <span>{matchBefore}</span>
      <em className={styles.descriptionMatch} data-testid="match">
        {match}
      </em>
      <span>{matchAfter}</span>
    </>
  );
};

const hasMatch = (string: string, subString: string) =>
  string.toLowerCase().includes(subString.toLowerCase());

export interface ChooseTemplateProps {
  capitalizedIntegrationName: string;
  loading: boolean;
  loadingError: boolean;
  availableTemplates?: IntegrationTemplate[];
  connectedIntegrations: IntegrationConnection[];
  multiple?: boolean;
  projectsByConnections: ProjectsByConnections;
  selectedTemplate: string;
  setSelectedTemplate: (templateId: string) => void;
  templateError?: string;
}

const ChooseTemplate = ({
  capitalizedIntegrationName,
  multiple = false,
  loading,
  loadingError,
  availableTemplates,
  connectedIntegrations,
  projectsByConnections,
  selectedTemplate,
  setSelectedTemplate,
  templateError
}: ChooseTemplateProps) => {
  const { t } = useTranslation();

  const templateRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!templateError) {
      return;
    }

    templateRef.current?.focus();
  }, [templateError, templateRef.current]);

  const [inputValue, setInputValue] = useState(
    availableTemplates?.find(({ id }) => selectedTemplate === id)?.name
  );
  const [matchedTemplates, setMatchedTemplates] = useState<
    IntegrationTemplate[]
  >([]);
  const [autoSelectedOption, setAutoSelectedOption] = useState<string>();

  const templateOptions = useMemo(() => {
    if (!availableTemplates || !Object.keys(projectsByConnections).length) {
      return null;
    }

    setMatchedTemplates([]);

    //array of ComboboxOption filtered by existence matches input value with option's title and/or description
    const comboboxFilteredOptions = availableTemplates.reduce<ReactNode[]>(
      (options, template) => {
        const onClick: MouseEventHandler<HTMLLIElement> = e => {
          e.stopPropagation();

          setSelectedTemplate(template.id);
          setInputValue(template.name);

          setAutoSelectedOption(undefined);
        };
        const selectedConnection = template.integration_connection_id || '';
        const baseUrl = connectedIntegrations.find(
          ({ id }) => id === selectedConnection
        )?.base_url;

        const project = projectsByConnections[selectedConnection]?.find(
          ({ id }) => template.project_id === id
        );
        const issueType = project?.issueTypes?.find(
          ({ id }) => template.issue_type_id === id
        );

        const projectName = project?.name || t(`Not found`);
        const issueTypeName = issueType?.name || t(`Not found`);

        const descriptionText = `${baseUrl} > ${projectName} > ${issueTypeName}`;

        const baseComboboxOptionProps = {
          key: template.id,
          value: template.name,
          formValue: template.id,
          onClick,
          ref:
            autoSelectedOption === template.id ? selectedOptionRef : undefined
        };

        if (!inputValue) {
          setMatchedTemplates(prev => [...prev, template]);

          options.push(
            <ComboboxOption
              {...baseComboboxOptionProps}
              description={descriptionText}
            >
              {template.name}
            </ComboboxOption>
          );
          return options;
        }

        const hasDescriptionMatch = hasMatch(descriptionText, inputValue);
        const hasOptionTitleMatch = hasMatch(template.name, inputValue);

        if (!hasOptionTitleMatch && !hasDescriptionMatch) {
          return options;
        }

        setMatchedTemplates(prev => [...prev, template]);

        const description = hasDescriptionMatch ? (
          <TextWithMatches text={descriptionText} inputValue={inputValue} />
        ) : (
          descriptionText
        );

        options.push(
          <ComboboxOption
            {...baseComboboxOptionProps}
            description={description}
          >
            {template.name}
          </ComboboxOption>
        );

        return options;
      },
      []
    );

    return comboboxFilteredOptions;
  }, [availableTemplates, inputValue, projectsByConnections]);

  useEffect(() => {
    if (autoSelectedOption && selectedOptionRef.current) {
      selectedOptionRef.current?.click();
    }
  }, [autoSelectedOption, selectedOptionRef.current]);

  if (loading) {
    const loadingLabel = multiple
      ? t('Share all to {{ capitalizedIntegrationName }} modal loading', {
          capitalizedIntegrationName
        })
      : t('Share to {{ capitalizedIntegrationName }} modal loading', {
          capitalizedIntegrationName
        });

    return (
      <div className={styles.chooseTemplateStep}>
        <Loader label={loadingLabel} />
      </div>
    );
  }

  if (loadingError) {
    const errorMessage = t(
      'Error loading project, issue type, and/or template data'
    );

    return (
      <div className={styles.chooseTemplateStep}>
        <p>{errorMessage}</p>
      </div>
    );
  }

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onBlur = () => {
    // if user has selected any template before, use this template
    if (selectedTemplate) {
      const selectedTemplateName =
        availableTemplates?.find(({ id }) => id === selectedTemplate)?.name ||
        '';

      setInputValue(selectedTemplateName);
    }

    //if user input something and has not selected any template before, set first matched template as selected
    if (inputValue && !selectedTemplate && matchedTemplates.length) {
      setInputValue(matchedTemplates[0].name || '');
      setAutoSelectedOption(matchedTemplates[0].id);
    }
  };

  return (
    <div className={styles.chooseTemplateStep}>
      <p>
        {multiple
          ? t(
              `Select the template to send the {{ axe }} issues to. This will create one {{ capitalizedIntegrationName }} ticket with all {{ axe }} filtered issues.`,
              {
                axe: ProductNames.axe,
                capitalizedIntegrationName
              }
            )
          : t(
              `Select the template to send the {{ axe }} issue to. This will create one {{ capitalizedIntegrationName }} ticket for the selected {{ axe }} issue.`,
              {
                axe: ProductNames.axe,
                capitalizedIntegrationName
              }
            )}
      </p>
      <Combobox
        label={t('{{capitalizedIntegrationName}} Template', {
          capitalizedIntegrationName
        })}
        value={inputValue}
        disabled={!availableTemplates?.length}
        error={templateError}
        ref={templateRef}
        onInput={onInput}
        placeholder={t('Choose a template')}
        required
        autocomplete="none"
        aria-autocomplete="list"
        onBlur={onBlur}
      >
        {templateOptions}
      </Combobox>
    </div>
  );
};

export default ChooseTemplate;
